import { AIArtifactType } from 'models/AIArtifact';
import AIJob from 'models/AIJob';
import Review from 'models/Review';
import Authorization, { AuthorizationOptions } from 'models/reviews/Authorization';
import Referral, { ReferralOptions } from 'models/reviews/Referral';
import ReviewModelFactory from 'models/reviews/ReviewModelFactory';
import Summarization from 'models/Summarization';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { summarizationQueryKeys } from 'services/api/summarizations';
import { queryClient } from 'services/http';
import {
  type AIJobNotificationContext,
  AIJobNotificationType,
  useAIJobNotificationStore,
} from 'stores/aiJobNotificationStore';
import { useNotificationStore } from 'stores/notificationStore';

import { simpleDateAtTime } from './date';

export const notificationQueryKeyMap = {
  [AIJobNotificationType.AUTHORIZATION_SUMMARY]: authorizationQueryKeys,
  [AIJobNotificationType.AUTHORIZATION_COMPARISON]: authorizationQueryKeys,
  [AIJobNotificationType.REFERRAL_SUMMARY]: referralQueryKeys,
  [AIJobNotificationType.WORKSPACE_SUMMARY]: summarizationQueryKeys,
};

/**
 * Generates the notification title based on job context and state
 */
export const getAIJobNotificationTitle = (
  context: AIJobNotificationContext,
  state: 'processing' | 'completed' | 'failed'
): string => {
  const description = getJobDescription(context);

  switch (state) {
    case 'processing':
      return `Currently processing ${description}`;
    case 'completed':
      return `Processing complete for ${description}`;
    case 'failed':
      return `Processing failed for ${description}`;
  }
};

/**
 * Gets the job description for the notification title
 */
const getJobDescription = (context: AIJobNotificationContext): string => {
  const { jobType, summaryType, patientName, createdAt } = context;

  switch (jobType) {
    case AIJobNotificationType.AUTHORIZATION_SUMMARY:
      return `${patientName} - Concurrent Review Summary`;

    case AIJobNotificationType.AUTHORIZATION_COMPARISON:
      return `${patientName} - Concurrent Review Comparison`;

    case AIJobNotificationType.REFERRAL_SUMMARY:
      return `${patientName} - Referral Summary`;

    case AIJobNotificationType.WORKSPACE_SUMMARY:
      return `${summaryType} - ${simpleDateAtTime(createdAt)}`;

    default:
      return `${jobType}`;
  }
};

/**
 * Checks if the current path matches the return URL for a job
 * Used to determine if we should hide the notification (user is on the loading page)
 */
export const isOnJobLoadingPage = (currentPath: string, context: AIJobNotificationContext): boolean => {
  // Normalize paths by removing trailing slashes
  const normalizedCurrentPath = currentPath.replace(/\/$/, '');
  const normalizedReturnUrl = context.returnUrl.replace(/\/$/, '');

  return normalizedCurrentPath === normalizedReturnUrl;
};

export type SummarizableReview = Authorization | Referral;
export type Jobbable = SummarizableReview | Summarization;

export const updateJobbableForAIJob = (job: AIJob, jobbableId: string, summaryType: AIJobNotificationType) => {
  if (job.isPending) return;

  const queryKey = notificationQueryKeyMap[summaryType].show({ id: jobbableId });

  if (job.isAwaiting) {
    const jobbable = queryClient.getQueryData(queryKey) as Jobbable | undefined;

    if (jobbable instanceof Review && jobbable?.summaryJob.isPending) {
      updateReviewCacheWithAIJob(queryKey, job);
    }

    return;
  }

  // We can't simply invalidate here because of this edge case:
  //   - User kicks off a summary for Patient A
  //   - User navigates to Patient B and kicks off another summary
  //   - User stays on Patient B's summary tab (loading state)
  //   - Notification for Patient A's completed summary appears, user clicks links
  //     - At this point, Patient A's review query is only marked as stale
  //   - User is taken to Patient A's summary tab, which *briefly displays the empty state*
  // The brief empty state is because the review refetched when it's needed, not before,
  // and the stale, cached review does not have a summary artifact yet
  queryClient.refetchQueries({ queryKey });
};

type SummarizableReviewOptions = AuthorizationOptions | ReferralOptions;

export const updateReviewCacheWithAIJob = (queryKey: any[], job: AIJob) => {
  const jobProperty =
    job.type === AIArtifactType.COMPARISON ? 'comparisonJobForCurrentSummary' : 'summaryJobForCurrentAttachments';

  queryClient.setQueryData(queryKey, (review: SummarizableReview) => {
    return ReviewModelFactory.get({
      ...review,
      [jobProperty]: job,
    } as SummarizableReviewOptions);
  });
};

/**
 * Updates the notification title for a tracked job by jobbableId.
 * Coordinates between aiJobNotificationStore and notificationStore.
 */
export const updateJobNotificationTitle = (jobbableId: string, newTitle: string) => {
  const { trackedJobs, jobNotificationMap, updateTrackedAIJob } = useAIJobNotificationStore.getState();
  const { notifications, actions } = useNotificationStore.getState();

  const jobEntry = Array.from(trackedJobs.entries()).find(([, context]) => context.jobbableId === jobbableId);
  if (!jobEntry) return;

  const [jobId, jobContext] = jobEntry;
  const notificationId = jobNotificationMap.get(jobId);
  if (!notificationId) return;

  const notification = notifications.find((n) => n.id === notificationId);
  if (!notification) return;

  updateTrackedAIJob(jobId, { summaryType: newTitle });
  actions.updateNotification(notificationId, {
    title: getAIJobNotificationTitle({ ...jobContext, summaryType: newTitle }, notification.state),
  });
};
